import { ChakraProvider, ColorModeScript, Box } from "@chakra-ui/react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Header from "./components/Header/Header.js";
import LandingSection from "./components/LandingSection";
import ProjectsSection from "./components/ProjectsSection";
import BlogSection from "./components/BlogSection";
import TestimonialsSection from "./components/TestimonialsSection";
import ContactMeSection from "./components/ContactMeSection/ContactMeSection.js";
import Footer from "./components/Footer";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetail from "./pages/projects/ProjectDetail";
import BlogPage from "./pages/BlogPage";
import BlogDetail from "./pages/blogs/BlogDetail";
import { AlertProvider } from "./context/alertContext";
import Alert from "./components/Alert";
import ErrorBoundary from "./components/ErrorBoundary";
import theme from "./theme";
import { Global, css } from '@emotion/react';
import NotFoundPage from './pages/NotFoundPage';
import ScrollToTop from './utils/ScrollToTop';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { getPageTitle, getMetaDescription } from './utils/pageTitle';

// Import required styles for react-notion-x globally
// import "react-notion-x/src/styles.css";
// import "./styles/notion-x.css";

function GlobalStyle() {
  return (
    <Global
      styles={css`
        /* Apply dark text to inputs by default except in contact form */
        select:not(#contactme-section select),
        option:not(#contactme-section option),
        input:not(#contactme-section input),
        textarea:not(#contactme-section textarea) {
          color: #2D3748 !important; /* gray.800 */
        }
        
        /* Style for contact form inputs to ensure they're white */
        #contactme-section input,
        #contactme-section textarea,
        #contactme-section select {
          color: white !important;
        }
        
        select option {
          background-color: white;
          color: #2D3748 !important;
        }
        
        /* Fix for Chakra UI selects */
        .chakra-select option {
          color: #2D3748 !important;
        }
      `}
    />
  );
}

function MainPage() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const sectionId = location.state?.scrollToSection;
    const scrollBehavior = location.state?.scrollBehavior || 'smooth'; // Default to smooth

    // Handle instant scroll to top if requested
    if (location.pathname === '/' && !sectionId && scrollBehavior === 'instant') {
      const timer = setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
        // Clear the state after scrolling
        navigate('.', { replace: true, state: {} });
      }, 0); // Use timeout 0 to ensure it runs after navigation commit
      return () => clearTimeout(timer);
    }

    // Handle scrolling to a specific section
    if (sectionId) {
      const element = document.getElementById(sectionId);
      if (element) {
        const offset = 80; // Consistent offset
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = element.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;

        const timer = setTimeout(() => {
          window.scrollTo({
            top: offsetPosition,
            behavior: scrollBehavior, // Use the determined behavior (can be 'instant' or 'smooth')
          });
          // Clear the state after scrolling
          navigate('.', { replace: true, state: {} });
        }, 100); // Keep delay for section scrolling to allow rendering

        return () => clearTimeout(timer);
      }
    }
  }, [location.pathname, location.state, navigate]); // Add location.pathname to dependencies

  return (
    <>
      <Helmet>
        <title>{getPageTitle()}</title>
        <meta name="description" content={getMetaDescription()} />
      </Helmet>
      <LandingSection />
      <ProjectsSection />
      <BlogSection />
      <TestimonialsSection />
      <ContactMeSection />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ChakraProvider theme={theme}>
        <ColorModeScript initialColorMode="dark" />
        <GlobalStyle />
        <ErrorBoundary>
          <AlertProvider>
            <Router>
              <ScrollToTop />
              <Box display="flex" flexDirection="column" minHeight="100vh">
                <Header />
                <Box as="main" flexGrow={1}>
                  <Routes>
                    <Route path="/" element={<MainPage />} />
                    <Route path="/projects" element={<ProjectsPage />} />
                    <Route path="/projects/:id" element={<ProjectDetail />} />
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/blog/:slug" element={<BlogDetail />} />
                    {/* <Route path="/logo-temp" element={<LogoPage />} /> */}
                    {/* <Route path="/notion-demo" element={<NotionBlogDemo />} /> */}
                    {/* <Route path="/notion-test" element={<NotionDemoTest />} /> */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </Box>
                <Footer />
                <Alert />
              </Box>
            </Router>
          </AlertProvider>
        </ErrorBoundary>
      </ChakraProvider>
    </HelmetProvider>
  );
}

export default App;
